import { useMemo, useSyncExternalStore } from 'react';
import { useAccountInfo } from '../api/queryHooks/account';
import { consentRegistry } from './registry';
import { activeServices } from './activeServices';
import { consentSnapshot, getConsentApi, subscribeConsent } from './consentState';

/**
 * Consent gate for React code.
 *   const { isAccepted } = useConsent();
 *   if (isAccepted('google_analytics')) <AnalyticsWidget />
 * `isAccepted` is false until the visitor answered AND the library is loaded;
 * it re-renders on every consent change. Non-React code (script loaders)
 * should not poll this — put the work in the service's onAccept instead.
 */
export function useConsent() {
  const { data: accountInfo } = useAccountInfo();
  useSyncExternalStore(subscribeConsent, consentSnapshot);
  const active = useMemo(() => activeServices(consentRegistry(), accountInfo), [accountInfo]);

  return {
    hasConsentableServices: active.length > 0,
    isAccepted: (id: string): boolean => {
      const api = getConsentApi();
      const svc = active.find((s) => s.id === id);
      return !!api && !!svc && api.acceptedService(id, svc.category);
    },
    openPreferences: (): void => { getConsentApi()?.showPreferences(); },
  };
}
